import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { WorkoutExercise, WorkoutExerciseUpdate } from '../types';
import { useInputFocus } from '../hooks/useInputFocus';
import WeightAdjuster from './WeightAdjuster';

interface EditWorkoutExerciseModalProps {
  workoutExercise: WorkoutExercise | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: WorkoutExerciseUpdate) => void;
  isLoading?: boolean;
}

const EditWorkoutExerciseModal: React.FC<EditWorkoutExerciseModalProps> = ({
  workoutExercise,
  isOpen,
  onClose,
  onSave,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<WorkoutExerciseUpdate>({
    sets: 3,
    reps: 10,
    weight: 0,
    rest_seconds: 60,
    notes: '',
  });

  // Обновляем форму при изменении упражнения
  useEffect(() => {
    if (workoutExercise) {
      setFormData({
        sets: workoutExercise.sets,
        reps: workoutExercise.reps,
        weight: workoutExercise.weight || 0,
        rest_seconds: workoutExercise.rest_seconds || 60,
        notes: workoutExercise.notes || '',
      });
    }
  }, [workoutExercise]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (field: keyof WorkoutExerciseUpdate, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInputFocus = useInputFocus();

  if (!isOpen || !workoutExercise) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4">
            Изменить упражнение
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Название упражнения (только для чтения) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Упражнение
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
              {workoutExercise.exercise?.name || 'Упражнение удалено'}
            </div>
          </div>

          {/* Параметры упражнения */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Подходы *
              </label>
              <input
                type="number"
                min="1"
                value={formData.sets}
                onChange={(e) => handleInputChange('sets', Number(e.target.value))}
                onFocus={handleInputFocus}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Повторения *
              </label>
              <input
                type="number"
                min="1"
                value={formData.reps}
                onChange={(e) => handleInputChange('reps', Number(e.target.value))}
                onFocus={handleInputFocus}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Вес (кг)
              </label>
              <WeightAdjuster
                weight={formData.weight || 0}
                onWeightChange={(weight) => handleInputChange('weight', weight)}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Отдых (сек)
              </label>
              <input
                type="number"
                min="0"
                value={formData.rest_seconds}
                onChange={(e) => handleInputChange('rest_seconds', Number(e.target.value))}
                onFocus={handleInputFocus}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Заметки */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Заметки
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Дополнительные заметки..."
            />
          </div>
        </form>

        {/* Кнопки */}
        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 w-full sm:w-auto"
          >
            Отмена
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Сохранение...</span>
              </>
            ) : (
              <span>Сохранить</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditWorkoutExerciseModal; 